import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { EnvelopeSimple, X, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STORAGE_KEY = 'pwf_newsletter_state';   // 'subscribed' | 'dismissed' | timestamp
const SHOW_AFTER_MS = 15_000;                 // 15 seconds after first paint
const RE_SHOW_DAYS = 14;                      // re-show 2 weeks after dismissal

function shouldShow() {
  const val = localStorage.getItem(STORAGE_KEY);
  if (!val) return true;
  if (val === 'subscribed') return false;     // never bother subscribers again
  // dismissed:TIMESTAMP_MS
  const m = val.match(/^dismissed:(\d+)$/);
  if (!m) return false;
  const dismissedAt = parseInt(m[1], 10);
  const days = (Date.now() - dismissedAt) / 86_400_000;
  return days > RE_SHOW_DAYS;
}

export default function NewsletterPopup() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!shouldShow()) return;
    const t = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, `dismissed:${Date.now()}`);
    setVisible(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post('/subscribe', { email: email.trim(), source: 'popup' });
      if (r.data?.already) {
        toast.info("You're already on the list!");
      } else if (r.data?.resubscribed) {
        toast.success("Welcome back — you're resubscribed.");
      } else {
        toast.success("You're on the list — we'll send the next pow wow digest your way.");
      }
      localStorage.setItem(STORAGE_KEY, 'subscribed');
      setVisible(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not subscribe — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-40 max-w-sm w-[calc(100vw-3rem)] bg-card border border-turquoise/40 rounded-2xl p-5 shadow-2xl shadow-turquoise/10 fade-up"
      data-testid="newsletter-popup"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground w-7 h-7 rounded-full hover:bg-muted grid place-items-center transition"
        aria-label="Dismiss newsletter signup"
        data-testid="newsletter-dismiss"
      >
        <X size={14} />
      </button>

      <div className="flex items-start gap-3 mb-3 pr-6">
        <div className="w-10 h-10 rounded-full bg-turquoise/15 grid place-items-center flex-shrink-0">
          <Sparkle size={20} weight="duotone" className="text-turquoise" />
        </div>
        <div>
          <p className="font-display text-lg leading-tight">Never miss a pow wow</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Free weekly digest of new events near you — drum contests, big payouts, road trip routes.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-2">
        <div className="relative">
          <EnvelopeSimple size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="pl-10 h-11"
            data-testid="newsletter-email-input"
          />
        </div>
        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-turquoise text-navy hover:bg-turquoise/90 rounded-full h-11 font-semibold"
          data-testid="newsletter-submit"
        >
          {submitting ? 'Adding you…' : 'Send me pow wow updates'}
        </Button>
        <p className="text-[10px] text-muted-foreground text-center pt-1">
          We email max once a week. Unsubscribe anytime.
        </p>
      </form>
    </div>
  );
}
